import { useCallback, useState } from 'react';
import {
    useCheckFeatureAccessQuery,
    useGrantChatbotAccessMutation,
} from '@/services/ApiService';
import { useUser } from '@/hooks/user/useUser';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useAiChatPanel } from '@/hooks/aiChat/useAiChatPanel';

export interface UseChatbotAccessResult {
    /** Whether the ZigBot entry point should render at all. */
    isVisible: boolean;
    /** Opens the chat, or the intro modal first when the user hasn't acknowledged it yet. */
    onActivate: () => void;
    isModalOpen: boolean;
    setIsModalOpen: (open: boolean) => void;
    grantChatbotAccess: ReturnType<typeof useGrantChatbotAccessMutation>[0];
    onAccessConfirmed: () => void;
}

/**
 * Resolves whether the current user can reach ZigBot, and what clicking it should do.
 *
 * ZigBot is available to every authenticated user regardless of age, so visibility turns only on
 * the feature flag and a loaded access state. Granted access opens the chat; anyone who hasn't
 * acknowledged the intro sees it first.
 */
export const useChatbotAccess = (): UseChatbotAccessResult => {
    const { user } = useUser();
    const { featureFlags } = useFeatureFlags();
    const { setIsOpen: openAiChat } = useAiChatPanel();
    const [isModalOpen, setIsModalOpen] = useState(false);

    // isLoading, not isFetching. The post-acknowledgement refetch would otherwise blink the icon
    // out and briefly hand an already-granted user the intro handler again.
    const { data: accessData, isLoading } = useCheckFeatureAccessQuery('chatbot', {
        skip: !user,
    });
    const [grantChatbotAccess] = useGrantChatbotAccessMutation();

    const onAccessConfirmed = useCallback(() => {
        openAiChat(true);
    }, [openAiChat]);

    const hasAccess = accessData?.hasAccess ?? false;

    const isVisible = Boolean(user) && !isLoading && featureFlags.FEATURE_FLAG_CHATBOT_ENABLED === true;

    const onActivate = useCallback(() => {
        if (hasAccess) {
            openAiChat(true);
            return;
        }
        setIsModalOpen(true);
    }, [hasAccess, openAiChat]);

    return {
        isVisible,
        onActivate,
        isModalOpen,
        setIsModalOpen,
        grantChatbotAccess,
        onAccessConfirmed,
    };
};
